import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { speechModelsEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { VOICE_PROVIDERS_ENDPOINT } from '@/hooks/useVoiceProviders';
import { isSpeechStrategyQuery } from '@/modules/aiAgents/hooks/useSpeechStrategy';
import {
  CreateSpeechModel,
  SpeechModel,
  SpeechModels,
  SpeechProviders,
  UpdateSpeechModel,
} from '@/modules/developerTools/types';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

type DeleteSpeechModel = { speech_model_id: string; force: boolean };

export const API = Object.freeze({
  listSpeechModels: async (): Promise<SpeechModels> => callGet(endpoints.speechModels),

  listProviders: async (): Promise<SpeechProviders> => callGet(endpoints.providers),

  createSpeechModel: async (speechModel: CreateSpeechModel): Promise<SpeechModel> =>
    callPost(endpoints.speechModels, speechModel),

  updateSpeechModel: async ({
    speech_model_id,
    ...speechModel
  }: UpdateSpeechModel): Promise<SpeechModel> =>
    callPut(endpoints.speechModel(speech_model_id), speechModel),

  deleteSpeechModel: async ({ speech_model_id, force }: DeleteSpeechModel) =>
    callDelete(`${endpoints.speechModel(speech_model_id)}${force ? '?force=true' : ''}`),
});

const useSpeechModels = () => {
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const accountSlug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'speech_models', actions.READ)
  );
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'speech_models', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'speech_models', actions.DELETE)
  );

  const {
    data: speechModels,
    status: listStatus,
    refetch: refetchList,
  } = useQuery<SpeechModels, Error>({
    queryKey: [endpoints.speechModels],
    queryFn: API.listSpeechModels,
    enabled: !!accountId && canRead,
  });

  const {
    data: providers,
    status: providersStatus,
    refetch: refetchProviders,
  } = useQuery<SpeechProviders, Error>({
    queryKey: [endpoints.providers],
    queryFn: API.listProviders,
    enabled: !!accountId && canRead,
  });

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: [endpoints.speechModels] });

  const { mutateAsync: createSpeechModel } = useMutation<
    SpeechModel,
    Error,
    CreateSpeechModel,
    { createdIn: string | null }
  >({
    mutationFn: API.createSpeechModel,
    onMutate: () => ({ createdIn: accountSlug }),
    onSuccess: (resp, _speechModel, context) => {
      // A form reopened before the refetch lands must already see the provider as used. The
      // list cache is not keyed by account, so skip it once the user switched to another one.
      if (context?.createdIn === accountSlug) {
        queryClient.setQueryData<SpeechModels>([endpoints.speechModels], (list) =>
          list ? { ...list, speech_models: [...list.speech_models, resp] } : list
        );
      }
      invalidateList();
      toast.success(t('developer_tools.speech_models.created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: updateSpeechModel } = useMutation<SpeechModel, Error, UpdateSpeechModel>({
    mutationFn: API.updateSpeechModel,
    onSuccess: (resp) => {
      invalidateList();
      // A new key or region changes the voices and models that the agent cards list
      queryClient.invalidateQueries({ queryKey: [VOICE_PROVIDERS_ENDPOINT, resp.speech_model_id] });
      toast.success(t('developer_tools.speech_models.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteSpeechModel, status: deleteStatus } = useMutation<
    unknown,
    ApiError,
    DeleteSpeechModel
  >({
    mutationFn: API.deleteSpeechModel,
    onSuccess: (_resp, { force }) => {
      invalidateList();
      // A forced delete also removes the model from the agent strategies that use it
      if (force) {
        queryClient.invalidateQueries({ predicate: isSpeechStrategyQuery });
      }
    },
  });

  return {
    speechModels: speechModels?.speech_models ?? [],
    listStatus,
    refetchList,
    providers: providers?.providers ?? [],
    defaults: providers?.defaults,
    providersStatus,
    refetchProviders,
    createSpeechModel,
    updateSpeechModel,
    deleteSpeechModel,
    deleteStatus,
    canWrite,
    canDelete,
  };
};

export default useSpeechModels;
